import { graphql } from 'react-relay';
import { useCallback, useEffect, useRef } from 'react';
import type {
	useSkipDraftSyncSkipDraftContentQuery as useSkipDraftSyncSkipDraftContentQuery$data,
	useSkipDraftSyncSkipDraftContentQueryVariables as useSkipDraftSyncSkipDraftContentQuery$variables,
} from './__types__/useSkipDraftSyncSkipDraftContentQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
type UseSkipDraftSyncProps = {
	contentId: string;
};
export const useSkipDraftSync = ({
	contentId,
}: UseSkipDraftSyncProps): {
	isEditorDirty: (currentStepVersion: number, currentTitle: string | null) => boolean;
} => {
	const originalStepVersion = useRef<undefined | number>(undefined);
	const originalTitle = useRef<undefined | string>(undefined);
	const { data } = useQueryInterop<
		useSkipDraftSyncSkipDraftContentQuery$data,
		useSkipDraftSyncSkipDraftContentQuery$variables
	>(
		graphql`
			query useSkipDraftSyncSkipDraftContentQuery($contentId: ID!) {
				singleContent(id: $contentId, status: ["draft"]) {
					id
					title
					version {
						ncsStepVersion
					}
				}
			}
		`,
		{
			variables: {
				contentId,
			},
			fetchPolicy: 'network-only',
		},
	);

	useEffect(() => {
		const stepVersion = data?.singleContent?.version?.ncsStepVersion;
		originalStepVersion.current = stepVersion ? parseInt(stepVersion) : undefined;
		originalTitle.current = data?.singleContent?.title ?? undefined;
	}, [data]);

	/**
	 * Returns true if either the latest NCS step version or title differ from
	 * the version/title of the published content.
	 * @param currentStepVersion the step version from the resolved editor state
	 * @param currentTitle the title from the resolved editor state
	 */
	const isEditorDirty = useCallback(
		(currentStepVersion: number, currentTitle: string | null): boolean => {
			if (!originalStepVersion.current || !originalTitle.current) {
				return true;
			}

			const stepVersionDiffers = currentStepVersion !== originalStepVersion.current;
			const titleDiffers = currentTitle !== originalTitle.current;

			return stepVersionDiffers || titleDiffers;
		},
		[],
	);

	return { isEditorDirty };
};
